import { PayrollDepartmentLeaderRole, PrivateUserInfoType, QuotaType } from '@otw/models'

/** Departments an employee can be assigned to for payroll/grouping. */
export const DEFAULT_DEPARTMENTS = ['sales', 'inventory', 'shipping'] as const
export type Department = string

export const DEPARTMENT_LABELS: Record<string, string> = {
  sales: 'Sales',
  inventory: 'Inventory',
  shipping: 'Shipping & Repairs',
}

const normalizeDepartment = (value: string): string => value.trim().toLowerCase()

export const getDepartmentLabel = (department: Department): string => {
  const normalized = normalizeDepartment(department)
  if (DEPARTMENT_LABELS[normalized]) return DEPARTMENT_LABELS[normalized]
  return department
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part[0].toUpperCase() + part.slice(1))
    .join(' ')
}

export const isValidDepartment = (value: unknown): value is Department =>
  typeof value === 'string' && value.trim().length > 0

export const getAllDepartments = (users: PrivateUserInfoType[]): Department[] => {
  const departments = new Set<string>(DEFAULT_DEPARTMENTS)
  users.forEach((user) => {
    const department = ((user as any).department as string | undefined)?.trim()
    if (!department) return
    departments.add(normalizeDepartment(department))
  })
  return Array.from(departments)
}

export const getUserDepartment = (user: PrivateUserInfoType): Department | null => {
  const department = ((user as any).department as string | undefined)?.trim()
  if (!department) return null
  const normalized = normalizeDepartment(department)
  return isValidDepartment(normalized) ? normalized : null
}

export const DEPARTMENT_LEADER_ROLE_LABELS: Record<PayrollDepartmentLeaderRole, string> = {
  manager: 'Manager',
  director: 'Director',
}

export const isPayrollEmployee = (user: PrivateUserInfoType): boolean =>
  user.role === 'admin' || user.role === 'babyAdmin'

export const getDepartmentLeaderRole = (
  user: PrivateUserInfoType,
): PayrollDepartmentLeaderRole | null => {
  const role = user.payroll_config?.department_leadership_commission?.role
  if (role === 'manager' || role === 'director') return role
  return null
}

export const isDepartmentLeader = (user: PrivateUserInfoType): boolean =>
  getDepartmentLeaderRole(user) !== null

/** Quota metric tracked for each department on the dashboard (when configured). */
export const getDepartmentQuotaType = (department: Department): QuotaType | null => {
  const normalized = normalizeDepartment(department)
  if (normalized === 'sales') return 'vehicles_sold'
  if (normalized === 'inventory') return 'inventory_trucks'
  return null
}

/** Other payroll employees in the leader's department (excludes the leader). */
export const getDepartmentPeersForLeader = (
  leader: PrivateUserInfoType,
  allUsers: PrivateUserInfoType[],
): PrivateUserInfoType[] => {
  const department = getUserDepartment(leader)
  if (!department || !isDepartmentLeader(leader)) return []

  return allUsers
    .filter(isPayrollEmployee)
    .filter((user) => user.id !== leader.id)
    .filter((user) => getUserDepartment(user) === department)
}
